import {
  Button as AriaButton,
  Menu,
  MenuItem,
  MenuTrigger,
  Popover,
  type Key,
} from 'react-aria-components';

import styles from './MenuButton.module.css';

export interface MenuButtonItem {
  id: string;
  label: string;
}

export interface MenuButtonProps {
  /** Visible button text, e.g. the facility name or the user's initials. */
  label: string;
  /** Accessible name when the visible label alone is not enough ("Account: Demo Admin"). */
  'aria-label'?: string;
  items: readonly MenuButtonItem[];
  onAction: (id: string) => void;
  /** Marks the current choice, for switchers such as the facility menu. */
  selectedId?: string;
  /** `avatar`: a round button showing the label (the user's initials), for the account menu. */
  variant?: 'default' | 'avatar';
}

export function MenuButton({
  label,
  items,
  onAction,
  selectedId,
  variant = 'default',
  ...aria
}: MenuButtonProps) {
  return (
    <MenuTrigger>
      <AriaButton
        className={styles.trigger}
        data-variant={variant}
        {...(aria['aria-label'] ? { 'aria-label': aria['aria-label'] } : {})}
      >
        <span className={styles.label}>{label}</span>
        {variant === 'avatar' ? null : <span className={styles.caret} aria-hidden="true" />}
      </AriaButton>
      <Popover className={styles.popover} placement="bottom end" offset={4}>
        <Menu
          className={styles.menu}
          items={items}
          onAction={(key: Key) => {
            onAction(String(key));
          }}
          {...(selectedId === undefined
            ? {}
            : { selectionMode: 'single' as const, selectedKeys: [selectedId] })}
        >
          {(item) => (
            <MenuItem id={item.id} className={styles.item} textValue={item.label}>
              {item.label}
            </MenuItem>
          )}
        </Menu>
      </Popover>
    </MenuTrigger>
  );
}
